// Single source of truth for video source handling across the app:
//   • HLS detection (Cloudflare Stream live replays AND Bunny Stream VOD)
//   • poster derivation (Cloudflare + Bunny still frames)
//   • HLS attach via hls.js (native HLS on Safari/iOS, resilient retry while a
//     freshly uploaded video is still transcoding)
//
// Used by the universal <VideoPlayer>, the Reels scroller and the Story viewer
// so every surface plays Cloudflare + Bunny the same way.
import { Capacitor } from "@capacitor/core";
import { resolveMediaUrl } from "@/components/FileUpload";
import { dataSaverActive } from "@/lib/mediaUrl";
import { liteVideoProfile, liteReason, liteHlsConfig, liteLevelCap, LITE_MAX_HEIGHT } from "@/lib/liteProfile";

// hls.js (≈150 KB gzip, 19 % du bundle) est chargé en CHUNK SÉPARÉ, hors du bundle de démarrage : l'app peint
// plus tôt, et le chunk est préchauffé juste après le 1er rendu du fil (preloadHls) → prêt avant la 1re vidéo.
let _Hls = null;
let _hlsLoading = null;
export const loadHls = () => {
  if (_Hls) return Promise.resolve(_Hls);
  if (!_hlsLoading) _hlsLoading = import("hls.js").then((m) => { _Hls = m.default || m; return _Hls; }).catch((e) => { _hlsLoading = null; throw e; });
  return _hlsLoading;
};
export const preloadHls = () => { loadHls().catch(() => {}); };
export const getHlsSync = () => _Hls;

// Équivalent SYNCHRONE de Hls.isSupported() sans charger la bibliothèque (MSE + codec H.264 + SourceBuffer).
export const mseSupported = () => {
  try {
    const MS = window.MediaSource || window.WebKitMediaSource || window.ManagedMediaSource;
    if (!MS) return false;
    if (typeof MS.isTypeSupported === "function" && !MS.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"')) return false;
    const SB = window.SourceBuffer || window.WebKitSourceBuffer;
    return !SB || !SB.prototype || (typeof SB.prototype.appendBuffer === "function" && typeof SB.prototype.remove === "function");
  } catch { return false; }
};

// Lecteur HLS natif RÉSERVÉ à Apple (iPhone / iPad / Safari). Chrome et le WebView Android ≥ 147 répondent
// aussi « maybe » à canPlayType(HLS) : sans ce garde, Android prenait la branche native → démarrage imposé sur
// la 1re variante du master Bunny (360p) et mémoire réseau / plafond écran ignorés. Hors Apple : hls.js dès que
// MSE est disponible (Android, Chrome, Firefox, Edge) ; natif seulement s'il n'y a aucun MSE.
const isAppleBrowser = () => {
  try {
    if (Capacitor.isNativePlatform()) return Capacitor.getPlatform() === "ios";
    return (navigator.vendor || "").includes("Apple") || /iPhone|iPad|iPod/.test(navigator.userAgent || "");
  } catch { return false; }
};

// Lot 556 — iPhone : hls.js via ManagedMediaSource (iOS 17.1+) au lieu du lecteur natif Apple, qui attend
// plusieurs segments avant la 1re image (≈ 740 ms mesurés vs ≈ 550 ms Android). Interrupteur admin
// settings.reels_ios_hlsjs (défaut ON) ; repli natif automatique sur l'appareil après 2 échecs hls.js avant la 1re image.
const IOS_HLSJS_KEY = "yalola.iosHlsJs";
const IOS_HLSJS_FAIL_KEY = "yalola.iosHlsJsFail";
let _iosHlsJs = true;
try { _iosHlsJs = localStorage.getItem(IOS_HLSJS_KEY) !== "0"; } catch { /* noop */ }
export const setIosHlsJs = (on) => {
  const next = on !== false;
  const changed = next !== _iosHlsJs;
  _iosHlsJs = next;
  // Réactivation par l'admin → on redonne sa chance à hls.js sur l'appareil (échecs oubliés).
  try { localStorage.setItem(IOS_HLSJS_KEY, next ? "1" : "0"); if (changed && next) localStorage.removeItem(IOS_HLSJS_FAIL_KEY); } catch { /* noop */ }
};
const iosHlsJsFailures = () => { try { return Number(localStorage.getItem(IOS_HLSJS_FAIL_KEY) || 0); } catch { return 0; } };
const noteIosHlsJsFailure = () => { try { localStorage.setItem(IOS_HLSJS_FAIL_KEY, String(iosHlsJsFailures() + 1)); } catch { /* noop */ } };
const noteIosHlsJsSuccess = () => { try { if (iosHlsJsFailures()) localStorage.removeItem(IOS_HLSJS_FAIL_KEY); } catch { /* noop */ } };
export const iosHlsJsUsable = () => {
  if (!_iosHlsJs || !isAppleBrowser()) return false;
  try { return !!window.ManagedMediaSource && mseSupported() && iosHlsJsFailures() < 2; } catch { return false; }
};
export const prefersNativeHls = (el) =>
  !!(el && el.canPlayType && el.canPlayType("application/vnd.apple.mpegurl")) && (!mseSupported() || (isAppleBrowser() && !iosHlsJsUsable()));

// A source is HLS when it's a Cloudflare Stream manifest OR any .m3u8 (Bunny, etc.).
export const isHlsSrc = (u) =>
  typeof u === "string" && (u.includes(".m3u8") || u.includes("cloudflarestream.com"));

// Still-frame poster derived from an HLS manifest URL.
//   Cloudflare Stream: .../manifest/video.m3u8            -> .../thumbnails/thumbnail.jpg
//   Bunny Stream:      https://{cdn}/{guid}/playlist.m3u8 -> https://{cdn}/{guid}/thumbnail.jpg
export const derivePosterFromHls = (resolvedUrl) => {
  if (!resolvedUrl) return "";
  if (resolvedUrl.includes("cloudflarestream.com")) {
    return resolvedUrl.replace(/\/manifest\/video\.m3u8.*$/, "/thumbnails/thumbnail.jpg");
  }
  if (resolvedUrl.includes(".m3u8")) {
    return resolvedUrl.replace(/\/[^/]*\.m3u8.*$/, "/thumbnail.jpg");
  }
  return "";
};

// Animated preview (a few-second looping WebP) served next to a Bunny HLS manifest —
// used for lightweight animated story/feed tiles straight off the Bunny CDN (no raw
// MP4 egress). Bunny only: https://{cdn}/{guid}/playlist.m3u8 -> .../preview.webp
export const derivePreviewFromHls = (resolvedUrl) => {
  if (!resolvedUrl || resolvedUrl.includes("cloudflarestream.com")) return "";
  if (resolvedUrl.includes(".m3u8")) {
    return resolvedUrl.replace(/\/[^/]*\.m3u8.*$/, "/preview.webp");
  }
  return "";
};

// Best (absolute) poster URL for a media src:
//   explicit poster  ->  HLS-derived still frame  ->  "" (caller falls back to neutral).
export const posterFor = (rawSrc, explicitPoster) => {
  if (explicitPoster) return resolveMediaUrl(explicitPoster);
  const resolved = resolveMediaUrl(rawSrc || "");
  if (isHlsSrc(resolved)) {
    const p = derivePosterFromHls(resolved);
    if (p) return p;
  }
  return "";
};

// Warm up the TCP+TLS connection to the video CDN before the first manifest
// request — shaves 100-300 ms off the very first video start.
const _preconnected = new Set();
export const preconnectVideoHost = (rawUrl) => {
  try {
    const u = new URL(resolveMediaUrl(rawUrl), window.location.href);
    if (_preconnected.has(u.origin)) return;
    _preconnected.add(u.origin);
    const l = document.createElement("link");
    l.rel = "preconnect";
    l.href = u.origin;
    l.crossOrigin = "anonymous";
    document.head.appendChild(l);
  } catch { /* noop */ }
};

// ---- Mémoire réseau (façon TikTok/YouTube) : la vidéo DÉMARRE à la qualité que le réseau permet.
// La bande passante MESURÉE pendant la lecture précédente (jusqu'à ≈ 1080p) sert de point de départ ;
// hls.js plafonne ensuite à la taille réelle du cadre (capLevelToPlayerSize : jamais plus de pixels que
// l'écran n'en affiche → économie de bande passante à grande échelle). Réseau inconnu (toute 1re vidéo) →
// hls.js télécharge UN petit segment de mesure (basse qualité, non affiché) puis part directement à la bonne
// qualité. 2G / économiseur de données → la plus basse. La mesure est liée au type de réseau (Wi-Fi ↔ données
// mobiles) : un changement invalide la mémoire pour ne pas démarrer trop haut sur un réseau devenu faible.
// RÈGLE PRODUIT : pas de démarrage volontairement flou ni de « saut » de qualité visible — la fluidité et la
// netteté vidéo sont décisives pour un réseau social face aux concurrents.
const BW_KEY = "yalola.bw";
const BW_SEED_MAX = 12_000_000;   // ≈ 1080p (5 Mb/s sur l'échelle Bunny) avec marge
const BW_SEED_MIN = 400_000;
const BW_TTL_MS = 3 * 86400000;
const NETWORK_3G_CAP = 1_500_000; // le navigateur signale un lien « 3g » → on ne part pas au-dessus de ~480p
let _lastBwSave = 0;
let _connType = "unknown";        // natif : wifi | cellular | none | unknown (Capacitor Network)
const netKey = () => (Capacitor.isNativePlatform() ? _connType : (navigator.connection?.type || ""));
export const savedBandwidth = () => {
  try {
    const { bps, t, net } = JSON.parse(localStorage.getItem(BW_KEY) || "null") || {};
    if (!(bps > 0) || Date.now() - (t || 0) > BW_TTL_MS) return 0;
    const cur = netKey();
    return net && cur && net !== "unknown" && cur !== "unknown" && net !== cur ? 0 : bps;
  } catch { return 0; }
};
export const rememberBandwidth = (bps) => {
  const now = Date.now();
  if (!(bps > 0) || now - _lastBwSave < 2000) return;
  _lastBwSave = now;
  try { localStorage.setItem(BW_KEY, JSON.stringify({ bps: Math.round(Math.min(bps, BW_SEED_MAX)), t: now, net: netKey() })); } catch { /* noop */ }
};
// Lien 2G → on reste sur la qualité la plus basse (seul cas « lent » : le navigateur Android sous-estime
// souvent le réseau, un vrai 4G/5G peut être annoncé « 3g »).
export const isSlowNetwork = () => /(^|-)2g$/.test((navigator.connection || {}).effectiveType || "");
// Économiseur de données du téléphone, lien annoncé « 3g » ou réglage Yalola « Économiser mes données » → la mesure
// reste utilisée mais le départ est plafonné ≈ 480p (jamais forcé au plus bas : l'image doit rester regardable).
const cappedNetwork = () => { const c = navigator.connection || {}; return !!c.saveData || c.effectiveType === "3g" || dataSaverActive(); };
export const startBandwidthSeed = () => {
  if (isSlowNetwork()) return 0;
  const saved = savedBandwidth();
  if (!saved) return 0;
  const bps = cappedNetwork() ? Math.min(saved, NETWORK_3G_CAP) : saved;
  return bps < BW_SEED_MIN ? 0 : Math.min(bps, BW_SEED_MAX);
};
// Dernier départ observé (hls.js) : hauteur de la 1re image affichée + segment de mesure ou non.
let _lastStart = null;
// Dernière branche de lecture prise (hls.js / natif / mp4) — permet de vérifier à distance qu'Android est bien sur hls.js.
let _lastPlayer = null;
export const lastPlayerUsed = () => _lastPlayer;
const fmtMbps = (bps) => `${(bps / 1e6).toFixed(1).replace(".", ",")} Mb/s`;
// Ligne de diagnostic (Paramètres, admins) : « Vidéo : départ 720p (mesure) · lecteur hls.js · mémoire 8,2 Mb/s ·
// réseau 4g · économiseur non » — permet de comprendre à distance pourquoi une vidéo démarre à telle qualité.
export const videoNetworkDiag = () => {
  const c = navigator.connection || {};
  const saved = savedBandwidth();
  const start = _lastStart ? `départ ${_lastStart.height}p${_lastStart.tested ? " (après mesure)" : ""}` : "aucune lecture encore";
  const player = _lastPlayer ? ` · lecteur ${_lastPlayer}` : "";
  const lite = liteReason();
  return `Vidéo : ${start}${player} · mémoire ${saved ? fmtMbps(saved) : "aucune"} · réseau ${c.effectiveType || "?"}${netKey() ? ` ${netKey()}` : ""} · économiseur ${c.saveData ? "OUI" : "non"}${lite ? ` · profil allégé OUI (${lite})` : ""}`;
};

// ---- iPhone start rendition. Apple's native HLS player always begins with the FIRST
// variant of the master playlist (Bunny lists 360p first) and can't be tuned, so on the
// native iOS app the Bunny master is served through the backend with the wanted start
// rendition first (/api/hls/{guid}/playlist.m3u8 — see routes_hls.py). QUALITÉ D'ABORD, comme
// hls.js sur Android/web : l'app envoie le débit MESURÉ (`bw`, mémoire réseau ci-dessus, alimentée
// par le préchauffage reelsWarmup et les lectures hls.js) et la plus grande dimension de l'écran en
// pixels (`maxh`) → le backend place en tête la meilleure variante que le réseau ET l'écran permettent.
// Rien de mesuré → repli `start` : Wi-Fi 480p / données 360p. Lien 2G / économiseur → la plus basse.
// App native uniquement (iPhone ; filet de sécurité Android si la branche native était malgré tout prise).
const BUNNY_MASTER = /^https?:\/\/[^/]+\.b-cdn\.net\/([0-9a-f-]{36})\/playlist\.m3u8$/i;
if (Capacitor.isNativePlatform()) {
  import("@capacitor/network").then(({ Network }) => {
    Network.getStatus().then((s) => { _connType = s.connectionType; }).catch(() => {});
    Network.addListener("networkStatusChange", (s) => { _connType = s.connectionType; });
  }).catch(() => {});
}
export const nativeStartHeight = () => {
  if (!Capacitor.isNativePlatform()) return 0;
  return _connType === "wifi" ? 480 : 360;
};
export const iosStartQuery = (startHeight) => {
  const q = [`start=${startHeight}`];
  if (isSlowNetwork()) q.push("bw=1");
  else { const bw = startBandwidthSeed(); if (bw) q.push(`bw=${bw}`); }
  const scr = (typeof window !== "undefined" && window.screen) || {};
  const screenMax = Math.round(Math.max(scr.width || 0, scr.height || 0) * ((typeof window !== "undefined" && window.devicePixelRatio) || 1));
  const maxh = liteVideoProfile() ? Math.min(screenMax || LITE_MAX_HEIGHT, LITE_MAX_HEIGHT) : screenMax;  // lot 734 — iPhone ≤ 3 Go : 480p max
  if (maxh) q.push(`maxh=${maxh}`);
  return q.join("&");
};
export const tunedMasterUrl = (url, startHeight) => {
  const m = startHeight ? BUNNY_MASTER.exec(url) : null;
  return m ? `${process.env.REACT_APP_BACKEND_URL}/api/hls/${m[1]}/playlist.m3u8?${iosStartQuery(startHeight)}` : url;
};

// Attach an HLS source (Cloudflare / Bunny) to a <video> via hls.js, with native
// HLS (Safari/iOS) support and resilient retry while the manifest is still
// transcoding. Returns a cleanup function.
//   autoPlay      : boolean OR () => boolean gate (call .play() when ready)
//   onReady()     : a frame is playable
//   onProcessing(b): manifest not ready yet (transcoding) -> true, then false
//   retryMs       : delay before re-fetching a not-yet-ready manifest
//   hlsConfig     : extra hls.js config overrides
//   onHls(h)      : receives the hls.js instance (runtime tuning, e.g. buffer budget)
//   startHeight   : native iOS only — start rendition served first by the backend
//   startPosition : seconds — « reprise de lecture » : le premier segment chargé est CELUI de la
//                   reprise (jamais « segment 0 puis seek » qui gelait le Reel au démarrage)
export const attachHls = (el, rawUrl, { autoPlay = false, onReady, onProcessing, retryMs = 6000, hlsConfig, readyRef, onAutoplayBlocked, onHls, startHeight = 0, startPosition = 0 } = {}) => {
  const url = resolveMediaUrl(rawUrl);
  const shouldPlay = () => (typeof autoPlay === "function" ? !!autoPlay() : !!autoPlay);
  // Play; if the browser blocks UNMUTED autoplay (NotAllowedError), fall back to muted
  // playback so the video never freezes on a paused frame (Facebook-style sound-on default).
  // Any OTHER rejection (AbortError when pause() interrupts a pending play during a
  // swipe) must NOT restart playback: that replayed off-screen videos in the
  // background (ghost audio + bandwidth drain on mobile).
  const tryPlay = () => {
    const pr = el.play();
    if (pr && pr.catch) pr.catch((err) => {
      if (err?.name !== "NotAllowedError" || !shouldPlay()) return;
      if (!el.muted && onAutoplayBlocked) { el.muted = true; onAutoplayBlocked(); el.play().catch(() => {}); }
    });
  };
  let retry = null;
  let played = false;
  // Readiness is SHARED with the <video> element's onPlaying/onLoadedData (via
  // `readyRef`) so that once a frame has visibly played, an error at the END of a
  // looping video recovers SILENTLY and never re-shows the "processing" overlay.
  const isReady = () => (readyRef ? !!readyRef.current : played);
  const markReady = () => { played = true; if (readyRef) readyRef.current = true; onReady?.(); onProcessing?.(false); };

  // Native HLS — Apple only (iOS Safari / iOS WebView / Safari), see prefersNativeHls.
  const attachNative = () => {
    _lastPlayer = "natif";
    el.src = tunedMasterUrl(url, startHeight);
    // readyState HAVE_NOTHING : la position devient la « default playback start position »
    // → le lecteur natif démarre directement là (pas de lecture du début puis saut).
    if (startPosition > 0) { try { el.currentTime = startPosition; } catch { /* noop */ } }
    const onErr = () => {
      if (!isReady()) onProcessing?.(true);
      clearTimeout(retry);
      retry = setTimeout(() => { try { el.load(); if (shouldPlay()) tryPlay(); } catch { /* noop */ } }, isReady() ? 500 : retryMs);
    };
    const onOk = () => markReady();
    el.addEventListener("error", onErr);
    el.addEventListener("loadeddata", onOk);
    if (shouldPlay()) tryPlay();
    return () => { el.removeEventListener("error", onErr); el.removeEventListener("loadeddata", onOk); clearTimeout(retry); };
  };
  if (prefersNativeHls(el)) return attachNative();

  if (mseSupported()) {
    // hls.js arrive en chunk séparé : si déjà chargé → branchement immédiat ; sinon dès qu'il est là
    // (le nettoyage peut survenir avant : on n'attache alors rien). Échec de chargement → src direct.
    let cleanupHls = null;
    let cleanupNative = null;
    let cancelled = false;
    // iPhone (lot 556) : hls.js en échec avant la 1re image → lecteur natif Apple pour ce reel, échec mémorisé.
    const canNative = !!(el.canPlayType && el.canPlayType("application/vnd.apple.mpegurl")) && isAppleBrowser();
    const fallbackNative = canNative ? () => {
      if (cancelled) return;
      noteIosHlsJsFailure();
      try { cleanupHls?.(); } catch { /* noop */ }
      cleanupHls = null;
      try { el.removeAttribute("src"); while (el.firstChild) el.removeChild(el.firstChild); } catch { /* noop */ }
      cleanupNative = attachNative();
    } : null;
    const start = (Hls) => {
      if (cancelled) return;
      cleanupHls = attachWithHls(Hls, el, url, { shouldPlay, tryPlay, onProcessing, retryMs, hlsConfig, onHls, startPosition, isReady, markReady, fallbackNative });
    };
    const ready = getHlsSync();
    if (ready) start(ready);
    else loadHls().then(start).catch(() => { if (cancelled) return; if (fallbackNative) fallbackNative(); else { _lastPlayer = "mp4"; el.src = url; if (shouldPlay()) tryPlay(); } });
    return () => { cancelled = true; cleanupHls?.(); cleanupNative?.(); };
  }

  // No HLS support at all — set src directly and hope the browser copes.
  _lastPlayer = "mp4";
  el.src = url;
  return () => {};
};

const attachWithHls = (Hls, el, url, { shouldPlay, tryPlay, onProcessing, retryMs, hlsConfig, onHls, startPosition, isReady, markReady, fallbackNative = null }) => {
  let retry = null;
  let mediaErrors = 0;
  // Qualité d'abord, sans « saut » visible :
  //  • réseau connu (mesure mémorisée) → startLevel auto : hls.js part DIRECTEMENT à la meilleure
  //    qualité que la mesure permet (fetch du 1er segment borné à maxStarvationDelay = 4 s)
  //  • réseau inconnu → testBandwidth : un petit segment de mesure (non affiché) puis la bonne qualité
  //  • 2G / économiseur → la plus basse, tout de suite
  //  • capLevelToPlayerSize → jamais une définition supérieure au cadre réel (× densité d'écran)
  //  • startFragPrefetch    → fetch the first fragment before play() so a swipe is instant
  //  • bigger forward buffer → no mid-clip stalls once playing
  const seed = startBandwidthSeed();
  const slow = isSlowNetwork();
  _lastPlayer = "hls.js";
  const extra = typeof hlsConfig === "function" ? hlsConfig() : hlsConfig; // évalué au branchement (état actif à jour)
  const lite = liteVideoProfile(); // lot 734 — téléphone à faible mémoire : tampons réduits + 480p max (le réglage explicite de l'appelant prime)
  const h = new Hls({
    startLevel: slow ? 0 : -1,
    testBandwidth: !slow && !seed,
    abrEwmaDefaultEstimate: seed || 500000,
    capLevelToPlayerSize: true,
    backBufferLength: 15,
    maxBufferLength: 30,
    maxMaxBufferLength: 60,
    lowLatencyMode: false,
    startFragPrefetch: true,
    ...(lite ? liteHlsConfig() : {}),
    ...(startPosition > 0 ? { startPosition } : {}),
    ...(extra || {}),
  });
  onHls?.(h);
  h.loadSource(url);
  h.attachMedia(el);
  h.on(Hls.Events.MANIFEST_PARSED, () => {
    if (lite) { const cap = liteLevelCap(h.levels); if (cap >= 0) { try { h.autoLevelCapping = cap; } catch { /* noop */ } } }
    onProcessing?.(false); if (shouldPlay()) tryPlay();
  });
  // La 1re mesure peut venir du cache HTTP (segment préchauffé) → on ne mémorise qu'à partir du 2e segment.
  let buffered = 0;
  h.on(Hls.Events.FRAG_BUFFERED, (_e, data) => {
    markReady();
    if (fallbackNative) noteIosHlsJsSuccess();
    if (++buffered === 1) { const lv = h.levels?.[data?.frag?.level]; if (lv) _lastStart = { height: Math.min(lv.width, lv.height) || lv.height, tested: !seed && !slow }; }
    if (buffered >= 2) rememberBandwidth(h.bandwidthEstimate);
  });
  h.on(Hls.Events.ERROR, (_e, data) => {
    if (!data?.fatal) return;
    // Already played (e.g. a looping video re-buffering at the end) → recover SILENTLY.
    if (isReady()) {
      try { if (data.type === Hls.ErrorTypes.MEDIA_ERROR) h.recoverMediaError(); else h.startLoad(); } catch { /* noop */ }
      return;
    }
    if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
      // iPhone : 2e erreur média avant la 1re image (ManagedMediaSource) → lecteur natif Apple pour ce reel.
      if (fallbackNative && ++mediaErrors >= 2) { fallbackNative(); return; }
      try { h.recoverMediaError(); return; } catch { /* noop */ }
    }
    if (fallbackNative && data.type !== Hls.ErrorTypes.NETWORK_ERROR) { fallbackNative(); return; }
    // NETWORK / other fatal → manifest may still be transcoding: keep retrying.
    onProcessing?.(true);
    clearTimeout(retry);
    retry = setTimeout(() => { try { h.loadSource(url); h.startLoad(); } catch { /* noop */ } }, retryMs);
  });
  return () => { clearTimeout(retry); try { h.destroy(); } catch { /* noop */ } };
};
